import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
  Stack,
  Paper
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  AccountCircle as AccountCircleIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import { getDeviceToken, setDeviceToken } from '../utils/deviceToken';

interface LoginProps {
  onLogin: (data: any, rememberMe: boolean) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(false);
  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  // Email OTP fallback state — used when the user cannot reach their authenticator app
  const [emailOtpMode, setEmailOtpMode] = useState(false);
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [info, setInfo] = useState<string | null>(null);

  // Forgot-password flow (in-page, before login).
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // Device-trust flow (new device → email code → trust 30 days).
  const [deviceMode, setDeviceMode] = useState(false);
  const [deviceCode, setDeviceCode] = useState('');
  const [deviceEmailMasked, setDeviceEmailMasked] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(apiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      // Parse the JSON body regardless of status so we can inspect error codes.
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Backend signals a DB cold-start / unavailability with HTTP 503 and
        // code "DB_UNAVAILABLE". Show a DB-specific message instead of the
        // misleading credentials error.
        if (response.status === 503 && data?.code === 'DB_UNAVAILABLE') {
          setError(data.error || 'Database currently not available. Please try again in a minute.');
        } else if (response.status === 423) {
          // Account locked due to MFA failures
          setError(data?.error || 'Account temporarily locked. Please try again later.');
        } else {
          setError('Invalid email or password. Please try again.');
        }
        return;
      }

      // MFA required
      if (data.mfa_required) {
        setMfaRequired(true);
        if (data.mfa_setup) {
          setMfaSetup(true);
          setMfaQrCode(data.qr_code);
          setMfaSecret(data.secret);
        }
        return;
      }

      onLogin(data, rememberMe);
    } catch (err) {
      // Network error or fetch failure (server unreachable, DNS, etc.)
      setError('Cannot reach the server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // In email-OTP mode the code is checked against the emailed code;
      // otherwise it is verified against the authenticator app (TOTP).
      const endpoint = emailOtpMode
        ? '/api/mfa/verify-email-otp'
        : '/api/mfa/verify-login';

      const response = await fetch(apiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // Send the stored device token so a trusted device skips verification.
        body: JSON.stringify({ code: mfaCode, device_token: getDeviceToken() })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Invalid code. Please try again.');
        setLoading(false);
        return;
      }

      // New / untrusted device — backend emailed a code; switch to the
      // device-verification screen instead of completing login.
      if (data.device_verification_required) {
        setDeviceMode(true);
        setDeviceEmailMasked(data.email_masked || '');
        setDeviceCode('');
        setInfo(`A verification code has been sent to ${data.email_masked || 'your email'}.`);
        setLoading(false);
        return;
      }

      onLogin(data, rememberMe);
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      const response = await fetch(apiUrl('/api/mfa/verify-device-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: deviceCode })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Invalid code. Please try again.');
        setLoading(false);
        return;
      }

      // Device verified — persist the trust token so this device is
      // remembered for 30 days, then complete login.
      if (data.device_token) {
        setDeviceToken(data.device_token);
      }
      onLogin(data, rememberMe);
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendDeviceOtp = async () => {
    setError(null);
    setInfo(null);
    setEmailOtpSending(true);
    try {
      const response = await fetch(apiUrl('/api/mfa/resend-device-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || 'Failed to resend the code. Please try again.');
        return;
      }
      setInfo(`A new code has been sent to ${data.email_masked || 'your email'}.`);
    } catch (err) {
      setError('Could not resend the code. Please try again.');
    } finally {
      setEmailOtpSending(false);
    }
  };

  // Request a one-time code by email — fallback when the authenticator app
  // is unavailable. Switches the form into email-OTP entry mode on success.
  const handleRequestEmailOtp = async () => {
    setError(null);
    setInfo(null);
    setEmailOtpSending(true);

    try {
      const response = await fetch(apiUrl('/api/mfa/request-email-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send email code. Please try again.');
        return;
      }

      setEmailOtpMode(true);
      setMaskedEmail(data.email_masked || '');
      setMfaCode('');
      setInfo(`A 6-digit code has been sent to ${data.email_masked || 'your email'}.`);
    } catch (err) {
      setError('Could not send the email code. Please try again.');
    } finally {
      setEmailOtpSending(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      const response = await fetch(apiUrl('/api/account/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.error || 'Could not send the reset link. Please try again.');
        return;
      }

      // Generic success (we never reveal whether the email is registered).
      setForgotSent(true);
    } catch (err) {
      setError('Cannot reach the server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setError(null);
    setInfo(null);
    setForgotSent(false);
    setForgotEmail(email);
    setForgotMode(true);
  };

  const backToLogin = () => {
    setError(null);
    setInfo(null);
    setForgotMode(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '600px',
          height: '600px',
          background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
          borderRadius: '50%',
          top: '-300px',
          right: '-200px',
          opacity: 0.1,
          filter: 'blur(80px)'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '400px',
          height: '400px',
          background: 'linear-gradient(135deg, #12b59f 0%, #2f4ae1 100%)',
          borderRadius: '50%',
          bottom: '-150px',
          left: '-100px',
          opacity: 0.1,
          filter: 'blur(60px)'
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={800}>
          <Box>
            {/* Logo/Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                  mb: 2,
                  boxShadow: '0 8px 32px rgba(47, 74, 225, 0.3)'
                }}
              >
                <AccountCircleIcon sx={{ fontSize: 48, color: 'white' }} />
              </Paper>
              <Typography
                variant="h4"
                fontWeight={700}
                gutterBottom
                sx={{
                  color: '#1f2937',
                  mb: 1
                }}
              >
                Welcome Back
              </Typography>
          
            </Box>

            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: 'white',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
            >

            <CardContent sx={{ p: 4 }}>
              {forgotMode ? (
              <form onSubmit={handleForgotPassword}>
                <Stack spacing={3}>
                  {error && (
                    <Fade in>
                      <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
                        {error}
                      </Alert>
                    </Fade>
                  )}

                  {forgotSent ? (
                    <>
                      <Alert severity="success" sx={{ borderRadius: 2 }}>
                        If an account exists for that email, a password reset link has been sent.
                        Please check your inbox.
                      </Alert>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={backToLogin}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Back to Login
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: '#4b5563', textAlign: 'center' }}>
                        Enter your email and we'll send you a link to reset your password.
                      </Typography>

                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        autoFocus
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: '#2f4ae1' }} />
                            </InputAdornment>
                          )
                        }}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={loading || !forgotEmail}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
                        sx={{
                          py: 1.75,
                          background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                          textTransform: 'none',
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          borderRadius: 2
                        }}
                      >
                        {loading ? 'Sending...' : 'Send Reset Link'}
                      </Button>

                      <Box sx={{ textAlign: 'center' }}>
                        <Button
                          variant="text"
                          onClick={backToLogin}
                          disabled={loading}
                          sx={{ textTransform: 'none', color: '#2f4ae1', fontWeight: 600 }}
                        >
                          Back to Login
                        </Button>
                      </Box>
                    </>
                  )}
                </Stack>
              </form>
              ) : deviceMode ? (
              <form onSubmit={handleDeviceVerify}>
                <Stack spacing={3}>
                  {error && (
                    <Fade in>
                      <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
                        {error}
                      </Alert>
                    </Fade>
                  )}
                  {info && !error && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {info}
                    </Alert>
                  )}

                  <Typography variant="body2" sx={{ color: '#4b5563', textAlign: 'center' }}>
                    We don't recognise this device. Enter the verification code sent to{' '}
                    <strong>{deviceEmailMasked || 'your email'}</strong> to continue. This device
                    will then be trusted for 30 days.
                  </Typography>

                  <TextField
                    fullWidth
                    label="Device Verification Code"
                    value={deviceCode}
                    onChange={(e) => setDeviceCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                    disabled={loading}
                    inputProps={{ inputMode: 'numeric', style: { letterSpacing: '8px', textAlign: 'center', fontSize: '1.4rem' } }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || deviceCode.length !== 6}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
                    sx={{
                      py: 1.75,
                      background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                      textTransform: 'none',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: 2
                    }}
                  >
                    {loading ? 'Verifying...' : 'Verify Device & Sign In'}
                  </Button>

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="text"
                      onClick={handleResendDeviceOtp}
                      disabled={emailOtpSending}
                      sx={{ textTransform: 'none', color: '#2f4ae1', fontWeight: 600 }}
                    >
                      {emailOtpSending ? 'Sending...' : 'Resend code'}
                    </Button>
                  </Box>
                </Stack>
              </form>
              ) : !mfaRequired ? (
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  {error && (
                    <Fade in>
                      <Alert
                        severity="error"
                        onClose={() => setError(null)}
                        sx={{ borderRadius: 2 }}
                      >
                        {error}
                      </Alert>
                    </Fade>
                  )}

                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                    autoFocus
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#2f4ae1' }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#2f4ae1'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2f4ae1',
                          borderWidth: 2
                        }
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#2f4ae1' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={loading}
                            size="small"
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#2f4ae1'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2f4ae1',
                          borderWidth: 2
                        }
                      }
                    }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          disabled={loading}
                          sx={{
                            color: '#2f4ae1',
                            '&.Mui-checked': {
                              color: '#2f4ae1'
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          Remember me
                        </Typography>
                      }
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                    sx={{
                      py: 1.75,
                      background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                      textTransform: 'none',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      boxShadow: '0 8px 24px rgba(47, 74, 225, 0.35)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2438b8 0%, #0e9179 100%)',
                        boxShadow: '0 12px 32px rgba(47, 74, 225, 0.45)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: '#94a3b8',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="text"
                      onClick={openForgot}
                      disabled={loading}
                      sx={{ textTransform: 'none', color: '#2f4ae1', fontWeight: 600 }}
                    >
                      Forgot password?
                    </Button>
                  </Box>
                </Stack>
              </form>
              ) : (
              <form onSubmit={handleMfaSubmit}>
                <Stack spacing={3}>
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <LockIcon sx={{ fontSize: 40, color: '#2f4ae1', mb: 1 }} />
                    <Typography variant="h6" fontWeight={600} color="#1f2937">
                      {emailOtpMode
                        ? 'Email Verification'
                        : (mfaSetup ? 'Setup Authenticator' : 'Two-Factor Authentication')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {emailOtpMode
                        ? `Enter the 6-digit code sent to ${maskedEmail || 'your email'}`
                        : (mfaSetup
                          ? 'Scan the QR code with Google Authenticator or Microsoft Authenticator, then enter the code'
                          : 'Enter the 6-digit code from your authenticator app')}
                    </Typography>
                  </Box>

                  {/* QR Code for first-time setup — hidden in email-OTP mode */}
                  {!emailOtpMode && mfaSetup && mfaQrCode && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <img src={mfaQrCode} alt="MFA QR Code" style={{ width: 180, height: 180 }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
                        Can't scan? Enter this key manually: <strong>{mfaSecret}</strong>
                      </Typography>
                    </Box>
                  )}

                  {error && (
                    <Fade in>
                      <Alert
                        severity="error"
                        onClose={() => setError(null)}
                        sx={{ borderRadius: 2 }}
                      >
                        {error}
                      </Alert>
                    </Fade>
                  )}

                  {info && (
                    <Fade in>
                      <Alert
                        severity="success"
                        onClose={() => setInfo(null)}
                        sx={{ borderRadius: 2 }}
                      >
                        {info}
                      </Alert>
                    </Fade>
                  )}

                  <TextField
                    fullWidth
                    label={emailOtpMode ? 'Email Code' : 'Authentication Code'}
                    value={mfaCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setMfaCode(val);
                    }}
                    required
                    autoFocus
                    disabled={loading}
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem', fontWeight: 700 }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#2f4ae1' }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#2f4ae1' },
                        '&.Mui-focused fieldset': { borderColor: '#2f4ae1', borderWidth: 2 }
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || mfaCode.length !== 6}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                    sx={{
                      py: 1.75,
                      background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                      textTransform: 'none',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      boxShadow: '0 8px 24px rgba(47, 74, 225, 0.35)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2438b8 0%, #0e9179 100%)',
                        boxShadow: '0 12px 32px rgba(47, 74, 225, 0.45)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': { background: '#94a3b8', boxShadow: 'none' }
                    }}
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>

                  {/* Fallback options — let the user switch between the
                      authenticator app and an emailed one-time code. */}
                  {!emailOtpMode ? (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleRequestEmailOtp}
                      disabled={emailOtpSending || loading}
                      startIcon={emailOtpSending ? <CircularProgress size={14} color="inherit" /> : <EmailIcon sx={{ fontSize: 16 }} />}
                      sx={{ textTransform: 'none', color: '#2f4ae1' }}
                    >
                      {emailOtpSending ? 'Sending code...' : "Can't access your authenticator? Email me a code"}
                    </Button>
                  ) : (
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleRequestEmailOtp}
                        disabled={emailOtpSending || loading}
                        sx={{ textTransform: 'none', color: '#2f4ae1' }}
                      >
                        {emailOtpSending ? 'Sending...' : 'Resend code'}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => { setEmailOtpMode(false); setMfaCode(''); setError(null); setInfo(null); }}
                        disabled={loading}
                        sx={{ textTransform: 'none', color: '#6b7280' }}
                      >
                        Use authenticator app instead
                      </Button>
                    </Stack>
                  )}

                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setMfaRequired(false);
                      setMfaCode('');
                      setError(null);
                      setInfo(null);
                      setEmailOtpMode(false);
                      setMaskedEmail('');
                    }}
                    sx={{ textTransform: 'none', color: '#2f4ae1' }}
                  >
                    Back to login
                  </Button>
                </Stack>
              </form>
              )}

              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  © 2025 All rights reserved
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    bgcolor: 'rgba(47, 74, 225, 0.06)',
                    border: '1px solid rgba(47, 74, 225, 0.15)',
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 14, color: '#2f4ae1' }} />
                  <Typography
                    variant="caption"
                    sx={{ color: '#2f4ae1', fontWeight: 600, fontSize: '0.7rem' }}
                  >
                    Secure login with MFA
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Login;
